import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function BlockLayout({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp } = useOrientation(orientation)

  return (
    <AbsoluteFill className="">
      <div className="flex items-center justify-between p-[50px] pb-[87px]">
        <div className="flex shrink-0 items-center space-x-[30px] rounded-full">
          <FallbackImg
            src={speaker?.avatarUrl}
            className="aspect-square h-[70px] rounded-full object-cover"
          />

          <p
            style={{ ...style.speakerUsername }}
            className={cn(
              'line-clamp-1 font-bold',
              s && 'text-[38px] leading-[49px]',
              lp && 'text-[40px] leading-[52px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        {clipScene.showZealousLogo && (
          <ZealousSvg
            color={style.logo.color}
            className="h-[49px] shrink-0 opacity-50"
          />
        )}
      </div>

      <div
        className={cn(
          'flex h-full items-center',
          s && 'px-[70px]',
          lp && 'px-[90px]',
        )}
      >
        {children}
      </div>

      <div>
        <BarsVisualization
          className={cn(p && 'h-[380px]', s && 'h-[300px]', l && 'h-[250px]')}
          color={style.waveform.color}
          gapSize={4}
          lineThickness={5}
          mediaStart={clipScene.mediaStart}
          mediaUrl={sceneAudioSrc(clipScene)}
          waveformUrl={clipScene.waveformUrl}
          placement="under"
          roundness={0}
        />
      </div>

      <div className="flex items-center">
        <FallbackImg
          src={clipScene.imageUrl}
          className={cn(
            'aspect-square object-cover',
            p && 'h-[270px]',
            s && 'h-[240px]',
            l && 'h-[250px]',
          )}
        />

        <div
          className={cn(
            'font-bold',
            p && 'text-[40px]',
            s && 'text-[38px] leading-[142%]',
            l && 'px-[70px] text-[42px]',
            sp && 'px-[50px]',
            lp && 'leading-[150%]',
          )}
        >
          <p style={{ ...style.title }} className="line-clamp-1 text-left">
            {clipScene.title}
          </p>

          <p style={{ ...style.subtitle }} className="line-clamp-1 text-left">
            {clipScene.subtitle}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  )
}

export const blockLayoutInfo: LayoutInfo = {
  name: 'block',
  Component: BlockLayout,
  defaultScene: {
    ...defaultClipScene,
    layout: 'block',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'primary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'primary',
      highlightBgColor: 'accent',
      color: 'secondary',
      fontFamily: 'primary',
      scrollType: 'line-by-line',
      highlightType: 'last-word-bg',
      fontSize: 66,
      fontWeight: 700,
      linesPerPage: 3,
      lineHeight: 1.46,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    portrait: {
      fontSize: 76,
      lineHeight: 1.68,
      linesPerPage: 8,
    },
    landscape: {
      fontSize: 66,
      lineHeight: 1.68,
      linesPerPage: 3,
    },
  },
}
